import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "../lib/utils";

const buttonVariants = cva(
  "inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        destructiveOutline:
          "border border-destructive/30 bg-transparent text-destructive shadow-sm hover:border-destructive hover:bg-destructive/10 hover:text-destructive",
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        // SIM-1452: small uppercase pill for filter/toggle chips. Pairs with
        // `aria-pressed` for active state. Dimensions live in compoundVariants
        // below so they override `size: "default"`'s `h-10 px-4 py-2`.
        chip: "border border-border bg-card text-muted-foreground hover:bg-accent hover:text-foreground aria-pressed:bg-accent aria-pressed:text-foreground aria-pressed:ring-2 aria-pressed:ring-primary",
        // SIM-1452: full-width left-aligned row for Popover / context-menu
        // surfaces. Square corners (the menu container provides the outer
        // radius). Dimensions live in compoundVariants below.
        menuItem: "text-foreground hover:bg-accent",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
        xs: "h-6 w-6 rounded-md",
        compact: "h-8 w-8 rounded-md",
      },
    },
    // SIM-1452: chip + menuItem need their own dimensions, but the base size
    // (`default` → `h-10 px-4 py-2`) gets concatenated after the variant
    // classes, so it would win in tw-merge. compoundVariants are appended last
    // → they win the conflict cleanly without `!important`.
    compoundVariants: [
      {
        variant: "chip",
        size: "default",
        class: "h-6 rounded px-2 py-0 text-[10px] font-semibold uppercase tracking-wide",
      },
      {
        variant: "menuItem",
        size: "default",
        class:
          "block h-auto w-full justify-start rounded-none px-3 py-1.5 text-left text-sm font-normal",
      },
    ],
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

function Button({
  className,
  variant,
  size,
  asChild = false,
  ref,
  ...props
}: ButtonProps & { ref?: React.Ref<HTMLButtonElement> }) {
  const Comp = asChild ? Slot : "button";
  return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
}

export { Button, buttonVariants };
